import './polyfills'
import { QueryClientProvider } from '@tanstack/react-query'
import { createRoot } from 'react-dom/client'
import { BrandContextProvider } from '@jotta/ui/BrandProvider'
import { I18nProvider as JottaI18nProvider } from '@jotta/i18n'
import { queryClient } from '@jotta/query'
import {
  CookieConsentContext,
  CookieConsentModal,
} from '@jotta/ui/CookieConsentModal'
import { CookieDeclaration } from '@jotta/ui/CookieDeclaration'
import type { FC } from 'react'
import { useEffect } from 'react'
import { useState } from 'react'
import { env } from '@jotta/utils/env'
import { ModalContext } from '@jotta/ui/Modal'
import { plainJottacloudTheme } from '@jotta/ui/themes/jottacloud-plain'
import { nanoid } from 'nanoid'

const cssPath = `/webapp_static/css/JottaConsentTheme.chunk.css?v=${env.buildTime}`
if (Math.random() === -1) {
  // fake conditional to force the generation of a css chunk we can use directly from the shadow dom,
  // but is not added to the main document we're embedding inside.
  import(
    /* webpackChunkName: "JottaConsentTheme" */ '@jotta/ui/themes/jottacloud'
  )
}

function createShadowRoot<T extends Object>(
  container: Element,
  Component: FC<T>,
) {
  const appRoot = document.createElement('div')
  appRoot.className = 'jottacloud'

  const shadowRoot = container.attachShadow({
    mode: 'open',
  })
  shadowRoot.appendChild(appRoot)

  const reactRoot = createRoot(appRoot)

  return (props: T) => {
    reactRoot.render(
      <ModalContext.Provider value={appRoot}>
        <CookieConsentContext.Provider
          value={{
            cookieConsentNeeded: true,
            disableUnconsentedCookieDetection: true,
            requestCookieConsent: () => {},
          }}
        >
          <QueryClientProvider client={queryClient}>
            <BrandContextProvider theme={plainJottacloudTheme}>
              <link rel="stylesheet" type="text/css" href={cssPath} />
              <JottaI18nProvider>
                <Component {...props} />
              </JottaI18nProvider>
            </BrandContextProvider>
          </QueryClientProvider>
        </CookieConsentContext.Provider>
      </ModalContext.Provider>,
    )
  }
}

export function startCookieConsent() {
  const consentRoot = document.createElement('div')
  document.body.appendChild(consentRoot)

  const component = createShadowRoot(
    consentRoot,
    ({ forceOpenId }: { forceOpenId?: string }) => {
      const openState = useState(false)
      const [open, setOpen] = openState
      useEffect(() => {
        if (forceOpenId) {
          setOpen(true)
        }
      }, [forceOpenId, setOpen])
      return (
        <CookieConsentModal
          disableBlur
          openState={open ? openState : undefined}
        />
      )
    },
  )
  component({})

  return () => component({ forceOpenId: nanoid() })
}

export function mountCookieDeclaration(element: Element) {
  createShadowRoot(element, () => <CookieDeclaration />)({})
}
